/**
 * The drawing surface: pointer and keyboard handling on top of render.ts.
 *
 * Navigation works the way a map does, because that is what people already
 * know: drag with either button to pan, scroll to zoom at the cursor. There is
 * no pan mode to enter. Selection happens on release, and only if the pointer
 * did not move, so a drag is always a pan and a click is always a click.
 *
 * The previous version started a pan only when the hit test found nothing under
 * the cursor. That meant panning silently stopped working as soon as the plan
 * had rooms in it, since every point inside the building is inside a room.
 *
 * Dragging a corner likewise begins only once the pointer has actually moved,
 * so clicking a junction selects it instead of nudging it a pixel.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { distance } from '../model/geometry';
import { addOpening, drawWall, moveJunction } from '../model/graph';
import type { Room } from '../model/rooms';
import type { Level, Point } from '../model/types';
import { hitTest, levelBounds } from './hitTest';
import { render, type Selection } from './render';
import type { Tool } from './tools';
import {
  DEFAULT_VIEWPORT,
  fitTo,
  gridSnap,
  orthoSnap,
  pan,
  pixelsToMm,
  toModel,
  zoomAt,
  type Viewport,
} from './viewport';

/** Pick radius in screen pixels, converted to millimetres per zoom level. */
const PICK_TOLERANCE_PX = 10;
/** Positions round to this when nothing better presents itself. */
const GRID_MM = 10;
/** How far the pointer must travel before a press counts as a drag. */
const DRAG_THRESHOLD_PX = 4;
/** How far an arrow key moves the view. */
const NUDGE_PX = 60;

type Gesture =
  | { kind: 'pan'; last: Point }
  /** Pressed on nothing in particular: pans if moved, selects if not. */
  | { kind: 'press'; origin: Point; moved: boolean }
  /** Pressed on a corner: drags it if moved, selects it if not. */
  | { kind: 'junction'; id: string; origin: Point; moved: boolean };

export interface PlanCanvasProps {
  level: Level;
  rooms: Room[];
  tool: Tool;
  wallThickness: number;
  selection: Selection | null;
  backdrop: HTMLImageElement | null;
  showBackdrop: boolean;
  /** Changing this value frames the plan. */
  fitSignal: number;
  onSelect: (selection: Selection | null) => void;
  onEdit: (edit: (level: Level) => Level) => void;
}

export function PlanCanvas(props: PlanCanvasProps): React.ReactElement {
  const { level, rooms, tool, selection, onSelect, onEdit } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [viewport, setViewport] = useState<Viewport>(DEFAULT_VIEWPORT);

  const [pointer, setPointer] = useState<Point | null>(null);
  const [chainFrom, setChainFrom] = useState<Point | null>(null);
  const [lengthEntry, setLengthEntry] = useState('');
  const [shiftHeld, setShiftHeld] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [panning, setPanning] = useState(false);

  const gestureRef = useRef<Gesture | null>(null);
  const [previewLevel, setPreviewLevel] = useState<Level | null>(null);

  const displayLevel = previewLevel ?? level;
  const toleranceMm = pixelsToMm(viewport, PICK_TOLERANCE_PX);

  // --- sizing ---

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const frame = useCallback(() => {
    if (size.width === 0 || size.height === 0) return;
    setViewport(fitTo(levelBounds(level), size.width, size.height));
  }, [level, size.width, size.height]);

  useEffect(() => {
    frame();
    // Framing on the signal only; re-framing on every edit would take the view
    // away from the user mid-task.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.fitSignal, size.width, size.height]);

  // --- rendering ---

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.width === 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(size.width * dpr);
    canvas.height = Math.round(size.height * dpr);
    canvas.style.width = `${size.width}px`;
    canvas.style.height = `${size.height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    render(ctx, {
      level: displayLevel,
      rooms,
      viewport,
      width: size.width,
      height: size.height,
      backdrop: props.backdrop,
      showBackdrop: props.showBackdrop,
      selection,
      hoverWallId: null,
      preview: chainFrom && pointer ? { from: chainFrom, to: pointer } : null,
      snapPoint: tool === 'select' ? null : pointer,
      calibrationPoints: [],
    });
  }, [
    displayLevel,
    rooms,
    viewport,
    size,
    props.backdrop,
    props.showBackdrop,
    selection,
    chainFrom,
    pointer,
    tool,
  ]);

  // --- coordinates ---

  const eventPoint = useCallback((event: React.PointerEvent | React.WheelEvent): Point => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }, []);

  const snapModelPoint = useCallback(
    (screen: Point): Point => {
      const raw = toModel(viewport, screen);
      for (const junction of level.junctions) {
        if (distance(raw, junction) <= toleranceMm) return { x: junction.x, y: junction.y };
      }
      if (shiftHeld && chainFrom) return gridSnap(orthoSnap(chainFrom, raw), GRID_MM);
      return gridSnap(raw, GRID_MM);
    },
    [viewport, level.junctions, toleranceMm, shiftHeld, chainFrom],
  );

  const startPan = (at: Point) => {
    gestureRef.current = { kind: 'pan', last: at };
    setPanning(true);
  };

  // --- pointer ---

  const handlePointerDown = (event: React.PointerEvent) => {
    canvasRef.current?.setPointerCapture(event.pointerId);
    const screen = eventPoint(event);

    // Right and middle drag always pan, whatever the tool.
    if (event.button === 1 || event.button === 2) {
      startPan(screen);
      return;
    }
    if (event.button !== 0) return;
    if (spaceHeld || event.altKey) {
      startPan(screen);
      return;
    }

    const model = snapModelPoint(screen);

    switch (tool) {
      case 'select': {
        const hit = hitTest({ level, rooms, point: toModel(viewport, screen), toleranceMm });
        // Defer: a press that moves is a pan or a corner drag, a press that
        // does not is a selection.
        gestureRef.current =
          hit?.kind === 'junction'
            ? { kind: 'junction', id: hit.id, origin: screen, moved: false }
            : { kind: 'press', origin: screen, moved: false };
        break;
      }

      case 'wall': {
        if (!chainFrom) {
          setChainFrom(model);
        } else {
          const from = chainFrom;
          onEdit((current) => drawWall(current, from, model, props.wallThickness, toleranceMm));
          setChainFrom(model);
        }
        setLengthEntry('');
        break;
      }

      case 'door':
      case 'window':
      case 'opening': {
        const point = toModel(viewport, screen);
        const hit = hitTest({ level, rooms, point, toleranceMm });
        if (hit?.kind === 'wall') {
          onEdit((current) => addOpening(current, hit.id, point, tool).level);
        }
        break;
      }
    }
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    const screen = eventPoint(event);
    const gesture = gestureRef.current;

    if (gesture?.kind === 'pan') {
      setViewport((current) => pan(current, screen.x - gesture.last.x, screen.y - gesture.last.y));
      gesture.last = screen;
      return;
    }

    if (gesture && !gesture.moved) {
      if (distance(screen, gesture.origin) < DRAG_THRESHOLD_PX) {
        setPointer(snapModelPoint(screen));
        return;
      }
      gesture.moved = true;
      if (gesture.kind === 'press') {
        // Pan from where the press began, so the view does not jump by the
        // threshold distance.
        startPan(gesture.origin);
        return;
      }
    }

    if (gesture?.kind === 'junction') {
      const target = snapModelPoint(screen);
      setPreviewLevel(moveJunction(level, gesture.id, target));
      setPointer(target);
      return;
    }

    setPointer(snapModelPoint(screen));
  };

  const handlePointerUp = (event: React.PointerEvent) => {
    canvasRef.current?.releasePointerCapture(event.pointerId);
    const gesture = gestureRef.current;
    gestureRef.current = null;
    setPanning(false);
    setPreviewLevel(null);

    if (!gesture) return;

    if (gesture.kind === 'junction') {
      if (gesture.moved) {
        const target = snapModelPoint(eventPoint(event));
        // One history entry for the whole drag, not one per pointermove.
        onEdit((current) => moveJunction(current, gesture.id, target));
      } else {
        onSelect({ kind: 'junction', id: gesture.id });
      }
      return;
    }

    if (gesture.kind === 'press' && !gesture.moved) {
      const point = toModel(viewport, eventPoint(event));
      onSelect(hitTest({ level, rooms, point, toleranceMm }));
    }
  };

  const handleWheel = (event: React.WheelEvent) => {
    if (event.shiftKey) {
      setViewport((current) => pan(current, -event.deltaY, 0));
      return;
    }
    const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
    setViewport((current) => zoomAt(current, eventPoint(event), factor));
  };

  // --- keyboard ---

  const commitTypedLength = useCallback(() => {
    const millimetres = Number(lengthEntry);
    if (!chainFrom || !pointer || !Number.isFinite(millimetres) || millimetres <= 0) return;

    const current = distance(chainFrom, pointer);
    if (current < 1) return;

    const to = {
      x: chainFrom.x + ((pointer.x - chainFrom.x) / current) * millimetres,
      y: chainFrom.y + ((pointer.y - chainFrom.y) / current) * millimetres,
    };
    const from = chainFrom;
    onEdit((lvl) => drawWall(lvl, from, to, props.wallThickness, toleranceMm));
    setChainFrom(to);
    setPointer(to);
    setLengthEntry('');
  }, [lengthEntry, chainFrom, pointer, onEdit, props.wallThickness, toleranceMm]);

  useEffect(() => {
    const typingInAField = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      return (
        !!target &&
        (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable)
      );
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Shift') setShiftHeld(true);
      // Space has to keep inserting a space while a room name is being typed,
      // so the field check comes before anything that swallows it.
      if (typingInAField(event)) return;

      if (event.key === ' ') {
        setSpaceHeld(true);
        event.preventDefault();
        return;
      }
      if (event.key === 'Escape') {
        setChainFrom(null);
        setLengthEntry('');
        onSelect(null);
        return;
      }
      if (event.key === 'f' || event.key === 'F') {
        frame();
        return;
      }

      const nudges: Record<string, [number, number]> = {
        ArrowLeft: [NUDGE_PX, 0],
        ArrowRight: [-NUDGE_PX, 0],
        ArrowUp: [0, NUDGE_PX],
        ArrowDown: [0, -NUDGE_PX],
      };
      const nudge = nudges[event.key];
      if (nudge) {
        setViewport((current) => pan(current, nudge[0], nudge[1]));
        event.preventDefault();
        return;
      }

      if (tool === 'wall' && chainFrom) {
        if (/^[0-9]$/.test(event.key)) {
          setLengthEntry((current) => current + event.key);
          return;
        }
        if (event.key === 'Backspace') {
          setLengthEntry((current) => current.slice(0, -1));
          event.preventDefault();
          return;
        }
        if (event.key === 'Enter' && lengthEntry) {
          commitTypedLength();
        }
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Shift') setShiftHeld(false);
      if (event.key === ' ') setSpaceHeld(false);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [tool, chainFrom, lengthEntry, commitTypedLength, onSelect, frame]);

  // Leaving a tool must not strand its half-finished state.
  useEffect(() => {
    setChainFrom(null);
    setLengthEntry('');
  }, [tool]);

  const cursor = useMemo(() => {
    if (panning) return 'grabbing';
    if (spaceHeld) return 'grab';
    if (tool === 'wall') return 'crosshair';
    if (tool === 'select') return 'default';
    return 'cell';
  }, [panning, tool, spaceHeld]);

  const statusText = useMemo(() => {
    if (tool === 'wall' && chainFrom && pointer) {
      const metres = distance(chainFrom, pointer) / 1000;
      return lengthEntry ? `Length: ${lengthEntry} mm ⏎` : `${metres.toFixed(2)} m`;
    }
    return '';
  }, [tool, chainFrom, pointer, lengthEntry]);

  return (
    <div className="canvas-host" ref={containerRef}>
      <canvas
        ref={canvasRef}
        className="plan-canvas"
        style={{ cursor }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={() => setPointer(null)}
        onWheel={handleWheel}
        onDoubleClick={() => {
          setChainFrom(null);
          setLengthEntry('');
        }}
        onContextMenu={(event) => event.preventDefault()}
      />
      {statusText && <div className="canvas-status">{statusText}</div>}
      <div className="canvas-hint">drag to pan · scroll to zoom · F to fit</div>
    </div>
  );
}

export { levelBounds };
